import { Icon, Input, Menu } from '@eleo-rh/core-ui'
import { Progress, Spin } from 'antd'
import { createStaticStyles } from 'antd-style'
import Button from 'components/UI/CoreUIButton'
import { API_EXPORT_CONFIGURABLES, API_EXPORTS } from 'constants/api-v2'
import { useI18n } from 'context/I18nProvider'
import { useBlob } from 'hooks/binary'
import useSWR from 'hooks/swr'
import { type ReactNode, useState } from 'react'
import AsyncExport, { type RequestOptions } from './AsyncExport'

const classNames = createStaticStyles(({ css }) => ({
  root: css`
    .ant-dropdown-menu-item-group {
      .ant-dropdown-menu-item-group-list {
        margin: 0;

        .ant-dropdown-menu-item {
          padding: 0;

          &:not(:last-child) {
            border-bottom: var(--color-slate-100) solid 1px;
          }
        }
      }
      .ant-dropdown-menu-item-group-title {
        padding: 12px 16px;
        background-color: var(--color-slate-100);
      }
    }
  `,
}))

type ExportSection =
  | 'matrix'
  | 'needs'
  | 'trainingPlan'
  | 'trainingManagement'
  | 'sessions'
  | 'accreditations'
  | 'renewableTraining'
  | 'surveys'
  | 'trainingHistory'
  | 'perimeter'
  | 'populations'
  | 'roomBooking'
  | 'collective'
  | 'userManagement'
  | 'sessionPeriod'
  | 'accreditationManagement'

type ExportClickHandler = (args: {
  callback: (options?: RequestOptions) => Promise<boolean>
  options: RequestOptions
  label: string
}) => unknown

type ExportMenuItem = {
  key: string | number
  label: string
  exportType: string
  requestURL?: string
  exportConfigurable?: string
  onClick?: ExportClickHandler
}

interface ExportMenuProps {
  section: ExportSection
  onClick?: ExportClickHandler
  requestURL?: string
  getRequestURL?: (exportConfigurable: ExportConfigurable) => string
  icon?: ReactNode
  exportType?: string
  elementId?: number | string
  items?: ExportMenuItem[]
  hiddenLabels?: string[]
}

type ExportConfigurable = {
  '@id': string
  id: number
  label: string
  type: string
  base: boolean
  owned?: boolean
}

const ExportMenu = ({
  section,
  onClick,
  requestURL = API_EXPORTS,
  getRequestURL,
  exportType,
  elementId,
  items = [],
  hiddenLabels = [],
}: ExportMenuProps) => {
  const { i18n } = useI18n()
  const { downloadBlob } = useBlob()
  const [menuOpen, setMenuOpen] = useState(false)
  const [search, setSearch] = useState('')

  const { data } = useSWR<ExportConfigurable[]>(`${API_EXPORT_CONFIGURABLES}?section=${section}`)
  const exports = data?.filter(exportConfigurable => !hiddenLabels.includes(exportConfigurable.label))

  const menuExports = [
    ...(exports ?? []).map(exportConfigurable => ({
      key: exportConfigurable.id,
      label: exportConfigurable.label,
      exportType: exportType ?? exportConfigurable.type,
      exportConfigurable: exportConfigurable['@id'],
      requestURL: getRequestURL?.(exportConfigurable) ?? requestURL,
      onClick,
      base: exportConfigurable.base,
      owned: exportConfigurable.owned !== false,
    })),
    ...items.map(item => ({ ...item, base: true, owned: true })),
  ]

  const searchQuery = search.trim().toLocaleLowerCase()

  const filteredMenuExports = searchQuery
    ? menuExports.filter(exportItem => i18n(exportItem.label).toLocaleLowerCase().includes(searchQuery))
    : menuExports
  const exportGroups = [
    {
      label: 'export-configurable.group.standard',
      exports: filteredMenuExports.filter(exportItem => exportItem.base),
    },
    {
      label: 'export-configurable.group.custom',
      exports: filteredMenuExports.filter(exportItem => !exportItem.base && exportItem.owned),
    },
    {
      label: 'export-configurable.group.shared',
      exports: filteredMenuExports.filter(exportItem => !exportItem.base && !exportItem.owned),
    },
  ].filter(group => group.exports.length)

  if (!menuExports.length) return null

  if (exports?.length === 1 && !items.length) {
    const exportConfigurable = exports[0]
    const label = i18n(exportConfigurable.label)

    return (
      <AsyncExport
        requestURL={getRequestURL?.(exportConfigurable) ?? requestURL}
        exportType={exportType ?? exportConfigurable.type}
        exportConfigurable={exportConfigurable['@id']}
        exportName={label}
        elementId={elementId}
      >
        {({ startExport, status, reset }) => {
          const downloadURL = status?.url ?? status?.document?.url

          if (status?.status === 'ready' && downloadURL) {
            return (
              <Button
                variant="tertiary"
                leftIcon="arrow-down-to-bracket"
                onClick={async () => {
                  await downloadBlob(downloadURL)
                  reset()
                }}
              >
                {i18n('label.download')}
              </Button>
            )
          }

          return (
            <div>
              <Button
                variant="tertiary"
                leftIcon="arrow-up-from-bracket"
                loading={status?.status === 'started' || status?.status === 'pending'}
                disabled={status?.status === 'started' || status?.status === 'pending'}
                onClick={() => {
                  const options = {
                    method: 'post',
                    data: {
                      exportType: exportType ?? exportConfigurable.type,
                      exportConfigurable: exportConfigurable['@id'],
                      ...(elementId === undefined ? {} : { elementId }),
                    },
                  }
                  if (onClick) {
                    onClick({ callback: startExport, options, label: exportConfigurable.label })
                    return
                  }
                  startExport(options)
                }}
              >
                {i18n('action.export')} {label}
              </Button>
              {status?.status === 'pending' && (
                <Progress
                  percent={status.total ? Math.floor((100 * (status.processed ?? 0)) / status.total) : 0}
                  size="small"
                />
              )}
            </div>
          )
        }}
      </AsyncExport>
    )
  }

  return (
    <Menu
      open={menuOpen}
      onOpenChange={(open, { source }) => {
        if (source !== 'menu') {
          setMenuOpen(open)
          if (!open) setSearch('')
        }
      }}
      placement="bottomRight"
      trigger={['click']}
      classNames={classNames}
      items={exportGroups.map(group => ({
        type: 'group' as const,
        label: i18n(group.label),
        children: group.exports.map(exportItem => ({
          key: exportItem.key,
          label: (
            <AsyncExport
              requestURL={exportItem.requestURL ?? requestURL}
              exportType={exportItem.exportType}
              exportConfigurable={exportItem.exportConfigurable}
              exportName={i18n(exportItem.label)}
              elementId={elementId}
            >
              {({ startExport, status, reset }) => {
                const downloadURL = status?.url ?? status?.document?.url
                const label = i18n(exportItem.label)
                const itemWrapperClassname =
                  'flex items-center px-6 py-2 text-left cursor-pointer min-w-64 w-full min-h-14'

                if (status?.status === 'ready' && downloadURL) {
                  return (
                    <div className={`${itemWrapperClassname} justify-between`}>
                      <span>{label}</span>
                      <Button
                        variant="tertiary"
                        size="small"
                        aria-label={i18n('label.download')}
                        onClick={async event => {
                          event.stopPropagation()
                          await downloadBlob(downloadURL)
                          reset()
                        }}
                        singleIcon="arrow-down-to-bracket"
                      />
                    </div>
                  )
                }

                if (status?.status === 'started' || status?.status === 'pending') {
                  return (
                    <div className={`${itemWrapperClassname} flex-col justify-center gap-2`}>
                      <div className="flex items-center justify-between gap-4 w-full">
                        <span>{label}</span>
                        {status.status === 'started' && <Spin size="small" />}
                      </div>
                      {status.status === 'pending' && (
                        <Progress
                          percent={status.total ? Math.floor((100 * (status.processed ?? 0)) / status.total) : 0}
                          size="small"
                        />
                      )}
                    </div>
                  )
                }

                return (
                  <button
                    type="button"
                    data-status={status?.status === 'failed' ? 'error' : 'default'}
                    className={`${itemWrapperClassname} ${status?.status === 'failed' ? 'text-red-700' : ''}`}
                    onClick={event => {
                      event.stopPropagation()
                      const options = {
                        method: 'post',
                        data: {
                          exportType: exportItem.exportType,
                          ...(exportItem.exportConfigurable
                            ? { exportConfigurable: exportItem.exportConfigurable }
                            : {}),
                          ...(elementId === undefined ? {} : { elementId }),
                        },
                      }
                      if (exportItem.onClick) {
                        exportItem.onClick({ callback: startExport, options, label: exportItem.label })
                        return
                      }
                      startExport(options)
                    }}
                  >
                    {label}
                  </button>
                )
              }}
            </AsyncExport>
          ),
        })),
      }))}
    >
      <span>
        <Input
          aria-label={i18n('action.export')}
          allowClear
          placeholder={menuOpen ? i18n('label.searchplaceholder') : i18n('action.export')}
          prefix={menuOpen && <Icon name="magnifying-glass" size="md" />}
          suffix={<Icon name={menuOpen ? 'chevron-up' : 'chevron-down'} size="xs" />}
          value={search}
          onChange={event => setSearch(event.target.value)}
          onClick={event => {
            if (menuOpen) event.stopPropagation()
          }}
        />
      </span>
    </Menu>
  )
}

export default ExportMenu
